import * as React from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '@ministry/ui/internal';

import { ApiError } from '../lib/api-client.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';

/**
 * SCREEN_SPECS.md I24 — the four-way 403 split
 * (`FORBIDDEN_ROLE`/`_SCOPE`/`_OWNERSHIP`/`_CLEARANCE`, API_CONTRACT.md §7), shared rather
 * than the two divergent inline copies this build found: `FileDetailPage` had all four
 * cases handled inline, `FileRegisterPage` had only two of the four (`_OWNERSHIP` and
 * `_CLEARANCE` fell through to a generic message on that screen alone) — the exact
 * "rendered inline on two screens" gap the brief names. One component now carries the
 * copy for all four, plus `NOT_FOUND` and a generic fallback, so a new screen cannot
 * reintroduce the same partial split by accident.
 *
 * Deliberately NOT a full-page route by itself (`ROUTES` has no `/403` to navigate to) —
 * every real 403 in this app arises from a QUERY against a specific resource
 * (`GET /files`, `GET /appointments`, …), so the natural place to show it is inline,
 * inside that screen's own `PageHeader`/layout, exactly where `FileDetailPage` already put
 * it. A route-level 403 would only ever be reachable by typing a URL this app never links
 * to, which is not a real navigation this build has found.
 */
export interface ForbiddenStateProps {
  error: unknown;
  /** Where "back" goes. Defaults to the dashboard. */
  backTo?: string;
  backLabel?: string;
}

export function ForbiddenState({ error, backTo, backLabel }: ForbiddenStateProps): React.JSX.Element {
  const t = useT();

  let title = t('forbidden.generic.title');
  let body = t('forbidden.generic.body');

  if (error instanceof ApiError) {
    if (error.code === 'NOT_FOUND') {
      title = t('forbidden.notFound.title');
      body = t('forbidden.notFound.body');
    } else if (error.code === 'FORBIDDEN_ROLE') {
      title = t('forbidden.role.title');
      body = t('forbidden.role.body');
    } else if (error.code === 'FORBIDDEN_SCOPE') {
      title = t('forbidden.scope.title');
      body = t('forbidden.scope.body');
    } else if (error.code === 'FORBIDDEN_OWNERSHIP') {
      title = t('forbidden.ownership.title');
      body = t('forbidden.ownership.body');
    } else if (error.code === 'FORBIDDEN_CLEARANCE') {
      title = t('forbidden.clearance.title');
      body = t('forbidden.clearance.body');
    }
  }

  return (
    <EmptyState
      title={title}
      body={body}
      action={
        <Link to={backTo ?? ROUTES.dashboard} className="text-sm font-semibold text-text-link underline">
          {backLabel ?? t('forbidden.back')}
        </Link>
      }
    />
  );
}
